import React, { FC } from 'react';
import { graphql, Link } from 'gatsby';
import classnames from 'classnames';
import Container from 'react-bootstrap/Container';

import DangerouslySetInnerHtml from 'components/common/DangerouslySetInnerHtml';
import ProductCard from 'components/ProductCard';
import ArticleCards from 'components/ArticleCards';
import IconCustom from 'components/common/IconCustom';
import Button from 'components/common/Button';

import { IProductWithArticlesProperties } from './model';

import './ProductWithArticles.scss';

const ProductWithArticles: FC<IProductWithArticlesProperties> = ({
  mainTitle,
  columns,
  sectionBackground,
  articleList,
  productList,
}) => (
  <section
    className={classnames('product-with-articles', {
      [`product-with-articles--background-${sectionBackground?.label}`]: sectionBackground?.label,
    })}
  >
    <Container fluid>
      <DangerouslySetInnerHtml className="product-with-articles__title" html={mainTitle} />

      {columns?.length ? (
        <div className="product-with-articles__columns">
          {columns.map(
            ({ properties: { columnTitle, productImage, columnTitleLink, learnMore } }) => (
              <div
                className={classnames('product-with-articles__column', {
                  'product-with-articles__column--product':
                    productList?.nodes?.length && productImage,
                  'product-with-articles__column--articles':
                    articleList?.nodes?.length && !productImage,
                })}
                key={columnTitle}
              >
                <div className="product-with-articles__column-title-wrapper">
                  <DangerouslySetInnerHtml
                    className="product-with-articles__column-title"
                    html={columnTitle}
                  />
                  {columnTitleLink?.length ? (
                    <Button to={columnTitleLink?.[0].url} variant="learn-more">
                      {columnTitleLink?.[0].name}
                      <IconCustom icon="arrow_down" />
                    </Button>
                  ) : null}
                </div>
                {productList?.nodes?.length && productImage ? (
                  <div className="product-with-articles__product-wrapper">
                    {productList.nodes.map((product) => (
                      <ProductCard
                        key={product.link}
                        cardTitle={product.cardTitle}
                        tickItems={product.tickItems}
                        productImage={productImage}
                        productImageAlt={product.productImageAlt}
                        currentFormat={product.currentFormat}
                        link={product.link}
                        tags={product.tags}
                        cartFakeLink={product.cartFakeLink}
                        linkFamily={product.linkFamily}
                        familyCardTitle={product.familyCardTitle}
                        featureItems={product.featureItems}
                        className="product-card--wide-link"
                      />
                    ))}
                  </div>
                ) : null}

                {articleList?.nodes?.length && !productImage ? (
                  <ArticleCards articleList={articleList.nodes} />
                ) : null}

                {learnMore?.length ? (
                  <div className="product-with-articles__learn-more">
                    <Link to={learnMore[0].url} className="product-with-articles__learn-more-link">
                      {learnMore[0].name}
                    </Link>
                  </div>
                ) : null}
              </div>
            )
          )}
        </div>
      ) : null}
    </Container>
  </section>
);

export const query = graphql`
  fragment FragmentProductWithArticles on TBodyItemProperties {
    mainTitle
    sectionBackground {
      label
    }
    columns {
      properties {
        columnTitle
        columnTitleLink {
          name
          url
        }
        learnMore {
          name
          url
        }
        productImage {
          ...FragmentGatsbyImage
        }
      }
    }
  }
  fragment FragmentProductWithArticlesProps on TProductWithArticlesStructure {
    properties {
      mainTitle
      sectionBackground {
        label
      }
      columns {
        properties {
          columnTitle
          columnTitleLink {
            name
            url
          }
          learnMore {
            name
            url
          }
          productImage {
            ...FragmentGatsbyImage
          }
        }
      }
    }
    structure
  }
`;

export default ProductWithArticles;
